import * as React from 'react';
import { cn } from '@noor/lib/cn';
import { StarIcon } from './merchant-icons';

/**
 * Which palette the stars are drawn in.
 *
 * - `brand` — filled stars in the accent orange. The original treatment, kept
 *   as the default so every existing rating row is unchanged.
 * - `gold` — the design system's dedicated `--rating` gold, which is what the
 *   Figma rating rows actually specify ("Alert/Warning/200"). New surfaces
 *   should use this.
 */
export type StarRatingTone = 'brand' | 'gold';

export type StarRatingProps = Omit<React.ComponentProps<'div'>, 'children'> & {
  /** Number of filled stars (clamped to `[0, max]`). */
  value: number;
  /** Total number of stars. */
  max?: number;
  /** Star glyph edge length in pixels. */
  size?: number;
  /** Palette for the filled/empty glyphs. Defaults to `brand`. */
  tone?: StarRatingTone;
};

const TONES: Record<StarRatingTone, { filled: string; empty: string }> = {
  brand: { filled: 'text-primary', empty: 'text-border' },
  gold: { filled: 'text-rating', empty: 'text-rating-empty' },
};

/**
 * A read-only 5-star rating row. Purely presentational — the caller supplies
 * the value.
 */
function StarRating({
  className,
  value,
  max = 5,
  size = 14,
  tone = 'brand',
  ...props
}: StarRatingProps) {
  const filled = Math.max(0, Math.min(max, Math.round(value)));
  const dimension = { width: size, height: size } as const;
  const palette = TONES[tone];

  return (
    <div
      data-slot="star-rating"
      role="img"
      aria-label={`${filled} out of ${max}`}
      className={cn('flex items-center gap-2', className)}
      {...props}
    >
      {Array.from({ length: max }, (_, index) => (
        <StarIcon
          key={index}
          style={dimension}
          className={cn(index < filled ? palette.filled : palette.empty)}
        />
      ))}
    </div>
  );
}

export { StarRating };
